import type { Anomaly } from '@/types/anomaly';
import type { PolicyViolation } from '@/data/mockPolicyViolations';
import type { AnomalyWarning } from '@/services/aiService';

const ANOMALY_TYPE_MAP: Record<string, AnomalyWarning['type']> = {
  'daily-hours-exceeded': 'overtime-risk',
  'weekly-hours-exceeded': 'overtime-risk',
  'weekend-work-no-approval': 'weekend-work',
  'missing-description': 'duplicate-entry',
  'missing-activity-type': 'duplicate-entry',
  'hours-above-average': 'unusual-pattern',
  'unusual-project-allocation': 'unusual-pattern',
  'late-submission': 'missing-time',
  'first-project-entry': 'closed-project',
  'scheduled-booking-variance': 'unusual-pattern',
  'scheduled-booking-dismissed': 'unusual-pattern',
  'time-and-absence-conflict': 'time-and-absence-conflict',
};

const ANOMALY_POLICY_MAP: Record<string, string> = {
  'daily-hours-exceeded': 'daily-overage',
  'weekly-hours-exceeded': 'daily-overage',
  'weekend-work-no-approval': 'non-working-day',
  'missing-description': 'missing-hours',
  'missing-activity-type': 'missing-hours',
  'hours-above-average': 'daily-overage',
  'unusual-project-allocation': 'unusual-pattern',
  'late-submission': 'missing-hours',
  'first-project-entry': 'unusual-pattern',
  'scheduled-booking-variance': 'missing-hours',
  'scheduled-booking-dismissed': 'missing-hours',
  'time-and-absence-conflict': 'missing-hours',
};

const SEVERITY_RANK: Record<string, number> = { low: 0, medium: 1, high: 2 };

export type MinSeverity = 'all' | 'medium' | 'high';

/**
 * Unifies policy violations and detected anomalies into a single list of
 * `AnomalyWarning`s and filters it by the org-configured minimum severity.
 */
export function buildIssueWarnings(
  violations: PolicyViolation[],
  anomalies: Anomaly[],
  minSeverity: MinSeverity = 'all'
): AnomalyWarning[] {
  const unifiedWarnings: AnomalyWarning[] = [
    ...violations.map((v, i): AnomalyWarning => {
      const vt = v.violationType ?? '';
      const policyCheckType = vt.includes('legal') ? 'legal-maximum'
        : vt.includes('missing') ? 'missing-hours'
        : vt.includes('non-working') ? 'non-working-day'
        : 'daily-overage';
      const warningType: AnomalyWarning['type'] = vt.includes('legal') ? 'overtime-risk'
        : vt.includes('missing') ? 'missing-time'
        : vt.includes('non-working') ? 'weekend-work'
        : 'overtime-risk';
      return {
        id: `policy-${i}`,
        type: warningType,
        severity: v.severity,
        message: v.message,
        suggestion: v.details
          ? `Expected ${v.details.expected}h · Logged ${v.details.actual}h${v.details.limit ? ` · Limit ${v.details.limit}h` : ''}`
          : undefined,
        day: v.date,
        policyCheckType,
      };
    }),
    ...anomalies.map((a, i): AnomalyWarning => ({
      id: `anomaly-${i}`,
      type: ANOMALY_TYPE_MAP[a.type] ?? 'unusual-pattern',
      severity: a.severity,
      message: a.message,
      suggestion: a.details,
      day: a.day,
      policyCheckType: ANOMALY_POLICY_MAP[a.type] ?? 'data-quality',
    })),
  ];

  const minRank = minSeverity === 'high' ? 2 : minSeverity === 'medium' ? 1 : 0;
  return minRank === 0
    ? unifiedWarnings
    : unifiedWarnings.filter(w => (SEVERITY_RANK[w.severity] ?? 0) >= minRank);
}
